import {
  computed,
  ref,
  toValue,
  watch,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref,
} from 'vue'

import type { ChartType, ReportViewPreferenceV1, ReportViewType } from '../models/chart.types'
import {
  parseReportViewPreference,
  resolveReportViewSelection,
} from '../service/reportViewSelection'
import { useCustomizeStore } from '../stores/customizeStore'

import { useAccount, useWorkspace } from '@/features/account'

const PREFERENCE_KIND = 'viewPreferenceV1'

export interface UseReportViewStateOptions {
  id: MaybeRefOrGetter<string>
  partitionName?: MaybeRefOrGetter<string | undefined>
  defaultView: MaybeRefOrGetter<ReportViewType>
  defaultChartType: MaybeRefOrGetter<ChartType>
  allowedViews: MaybeRefOrGetter<readonly ReportViewType[]>
  availableChartTypes: MaybeRefOrGetter<readonly ChartType[]>
  catalogSettled: MaybeRefOrGetter<boolean>
}

export interface UseReportViewState {
  view: Ref<ReportViewType>
  chartType: Ref<ChartType>
  hasPreference: ComputedRef<boolean>
  hydrateChartType: (value: ChartType) => void
  setView: (value: ReportViewType) => void
  setChartType: (value: ChartType) => void
}

/** Reports-owned persistence seam. The stable partition includes account/workspace so one user's
 *  choice cannot leak across analytics contexts sharing the same section id. */
export function useReportViewState(options: UseReportViewStateOptions): UseReportViewState {
  const account = useAccount()
  const workspace = useWorkspace()
  const cache = useCustomizeStore()
  const view = ref<ReportViewType>(toValue(options.defaultView))
  const chartType = ref<ChartType>(toValue(options.defaultChartType))
  const hydratedPartition = ref<string | null>(null)
  const locallyChangedPartition = ref<string | null>(null)
  const remotelyHydratedPartition = ref<string | null>(null)
  let observedPartition: string | null | undefined
  const partition = computed(() => {
    const accountId = account.accountId.value
    const userId = account.userId.value
    const workspaceId = workspace.workspaceId.value
    if (userId === undefined || accountId === undefined || workspaceId === undefined) return null
    const reportPartition = toValue(options.partitionName) || 'default'
    return `report-view:${userId}:${accountId}:${String(workspaceId)}:${reportPartition}:${toValue(options.id)}`
  })

  function resolve(saved?: unknown): ReportViewPreferenceV1 {
    return resolveReportViewSelection({
      ...(saved === undefined ? {} : { saved }),
      defaultView: toValue(options.defaultView),
      defaultChartType: toValue(options.defaultChartType),
      allowedViews: toValue(options.allowedViews),
      availableChartTypes: toValue(options.availableChartTypes),
    })
  }

  function apply(selection: ReportViewPreferenceV1): void {
    view.value = selection.view
    chartType.value = selection.chartType
  }

  function applyProvisional(nextPartition: string | null): void {
    const saved = nextPartition
      ? parseReportViewPreference(cache.getCached<unknown>(nextPartition, PREFERENCE_KIND))
      : null
    const allowedViews = toValue(options.allowedViews)
    view.value =
      (saved && allowedViews.includes(saved.view) ? saved.view : null) ??
      (allowedViews.includes(toValue(options.defaultView)) ? toValue(options.defaultView) : null) ??
      allowedViews[0] ??
      toValue(options.defaultView)
    chartType.value = saved?.chartType ?? toValue(options.defaultChartType)
  }

  watch(
    [partition, () => toValue(options.catalogSettled)],
    ([nextPartition, catalogSettled]) => {
      hydratedPartition.value = null
      if (observedPartition !== nextPartition) {
        locallyChangedPartition.value = null
        remotelyHydratedPartition.value = null
        observedPartition = nextPartition
      }
      if (!catalogSettled) {
        applyProvisional(nextPartition)
        return
      }
      if (!nextPartition) {
        apply(resolve())
        return
      }
      apply(resolve(cache.getCached<unknown>(nextPartition, PREFERENCE_KIND)))
      hydratedPartition.value = nextPartition
    },
    { immediate: true },
  )

  // Capability refresh or caller config changes may invalidate an old selection.
  watch(
    () => [
      toValue(options.allowedViews),
      toValue(options.availableChartTypes),
      toValue(options.catalogSettled),
    ],
    ([, , catalogSettled]) => {
      if (!catalogSettled) return
      apply(resolve({ version: 1, view: view.value, chartType: chartType.value }))
    },
    { deep: true },
  )

  // `view` / `chartType` are controlled props on DataVisible, not mount-only defaults. Parent
  // updates take precedence, then reconcile through the same availability algorithm.
  watch(
    () => toValue(options.defaultView),
    (nextView) => {
      if (!toValue(options.catalogSettled)) {
        if (toValue(options.allowedViews).includes(nextView)) view.value = nextView
        return
      }
      apply(resolve({ version: 1, view: nextView, chartType: chartType.value }))
    },
  )
  watch(
    () => toValue(options.defaultChartType),
    (nextChartType) => {
      if (!toValue(options.catalogSettled)) {
        chartType.value = nextChartType
        return
      }
      apply(resolve({ version: 1, view: view.value, chartType: nextChartType }))
    },
  )

  watch(
    [partition, view, chartType, () => toValue(options.catalogSettled)],
    ([nextPartition, nextView, nextChartType, catalogSettled]) => {
      if (!nextPartition || hydratedPartition.value !== nextPartition || !catalogSettled) return
      cache.setCached<ReportViewPreferenceV1>(nextPartition, PREFERENCE_KIND, {
        version: 1,
        view: nextView,
        chartType: nextChartType,
      })
    },
    { flush: 'post' },
  )

  return {
    view,
    chartType,
    hasPreference: computed(() => {
      const currentPartition = partition.value
      if (!currentPartition) return false
      return (
        locallyChangedPartition.value === currentPartition ||
        parseReportViewPreference(cache.getCached<unknown>(currentPartition, PREFERENCE_KIND)) !==
          null
      )
    }),
    hydrateChartType: (value) => {
      const currentPartition = partition.value
      if (
        !currentPartition ||
        locallyChangedPartition.value === currentPartition ||
        remotelyHydratedPartition.value === currentPartition ||
        parseReportViewPreference(cache.getCached<unknown>(currentPartition, PREFERENCE_KIND)) !==
          null ||
        !toValue(options.availableChartTypes).includes(value)
      )
        return
      apply(resolve({ version: 1, view: view.value, chartType: value }))
      remotelyHydratedPartition.value = currentPartition
    },
    setView: (value) => {
      locallyChangedPartition.value = partition.value
      view.value = resolve({ version: 1, view: value, chartType: chartType.value }).view
    },
    setChartType: (value) => {
      locallyChangedPartition.value = partition.value
      chartType.value = resolve({ version: 1, view: view.value, chartType: value }).chartType
    },
  }
}
